import { Box, Divider, Flex, Portal, Text } from '@chakra-ui/core'
import React, { useCallback, useEffect, useRef } from 'react'
import { Button } from 'components/ui/Button'
import { useId } from '@reach/auto-id'

interface Props {
  onCancel: VoidFunction
  onConfirm: VoidFunction
  isOpen: boolean
  isLoading: boolean
  title?: string
  description?: string
  cancelButtonText?: string
  confirmButtonText?: string
}
const ConfirmationPopup: React.FC<Props> = ({
  isOpen,
  onCancel,
  onConfirm,
  isLoading,
  title,
  description,
  cancelButtonText,
  confirmButtonText,
}) => {
  const cancelRef = useRef<HTMLButtonElement | null>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const dialogId = `confirmation-${useId()}`

  const focusCancel = useCallback((node: HTMLButtonElement | null) => {
    cancelRef.current = node
    if (node) {
      returnFocusRef.current = document.activeElement as HTMLElement | null
      node.focus()
    }
  }, [])

  // Handle Escape key to close confirmation popup
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      if (!isLoading) onCancel()
    }

    // Capture before React's delegated handler can dismiss a surrounding modal.
    document.addEventListener('keydown', handleEscape, true)
    return () => document.removeEventListener('keydown', handleEscape, true)
  }, [isOpen, onCancel, isLoading])

  useEffect(() => {
    if (!isOpen) return
    return () => {
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus()
      returnFocusRef.current = null
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <Portal>
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="rgba(0, 0, 0, 0.5)"
        zIndex={9999}
        pointerEvents="auto"
      />
      <Flex
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return
          const target = event.shiftKey ? confirmRef.current : cancelRef.current
          const edge = event.shiftKey ? cancelRef.current : confirmRef.current
          if (document.activeElement === edge || isLoading) {
            event.preventDefault()
            if (!isLoading) target?.focus()
          }
        }}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        aria-describedby={description ? `${dialogId}-description` : undefined}
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="white"
        borderRadius="5px"
        flexDirection="column"
        alignItems="flex-start"
        p="16px"
        zIndex={10000}
        boxShadow="0px 4px 6px rgba(0, 0, 0, 0.1)"
        maxWidth={{ base: '95%', md: '500px' }}
        width="100%"
        pointerEvents="auto"
      >
        <Text id={`${dialogId}-title`} fontWeight="bold" fontSize="20px" mb="10px">
          {title}
        </Text>
        <Divider />
        <Text id={`${dialogId}-description`} fontSize="14px" color="gray.600" mb="20px">
          {description}
        </Text>
        <Flex justifyContent="flex-end" w="100%" style={{ columnGap: '10px' }}>
          <Button
            ref={focusCancel}
            onClick={onCancel}
            type="button"
            variant="secondary"
            bg="white"
            isDisabled={isLoading}
            borderColor="#dfe4e8"
            color="#222222"
            borderRadius="5px"
            _hover={{ bg: '#f5f6f8' }}
          >
            {cancelButtonText}
          </Button>
          <Button
            ref={confirmRef}
            onClick={onConfirm}
            type="button"
            variant="primary"
            isDisabled={isLoading}
            isLoading={isLoading}
            bg="#222222"
            color="white"
            borderRadius="5px"
            _hover={{ bg: '#333333' }}
          >
            {confirmButtonText}
          </Button>
        </Flex>
      </Flex>
    </Portal>
  )
}

export default ConfirmationPopup
